import {nanoid} from '@reduxjs/toolkit';
import {FormInstance} from 'antd/es/form';
import {debounce} from 'lodash-es';
import {
    Dispatch, MutableRefObject, SetStateAction, useCallback, useEffect, useRef, useState,
} from 'react';
import {useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';

import {EDITABLE_TABLE_LIST_NAME} from 'components/editable-table/model/constants';
import {IValidationController} from 'components/editable-table/model/types';
import {IEntityMeta, loadMetadata, selectMetadata} from 'entities/metadata';
import {TEntity} from 'modules/data';
import {fetchRequestListInfo} from 'modules/data/api/documents-upload-monitoring-api';
import {
    filterNonRenderableFieldsAndUpdateState as filterNonRenderableFieldsAndUpdateStateAction,
} from 'modules/data/data-actions';
import {EEntityType} from 'shared/constants/entities';
import {useEntityFromLocation} from 'shared/hooks/use-entity-from-location';
import {TStateSetter} from 'shared/types/generics';
import {showMessageFromResponse, showMessage} from 'shared/utils';
import {useAppDispatch} from 'store/config/hooks';
import {TAppState} from 'store/config/types';
import {
    clearAllFilesInfo,
    IDocumentTemplatesTypeResponse,
    IDocumentTypeResponse,
    IDocumentUploadFile,
    fetchFileAttachInfo,
    fetchFileUploadProperties,
    fetchTemplateTypesForFile,
    FILE_UNIQUE_FIELD_NAME,
    IFileAttachInfoResponse,
    filterFilesByExtensions,
    generateDefaultFileInfo,
    loadBranchesLinkingFlag,
    loadFileRowStateDataInEditableTable,
    NO_FILES_MESSAGE,
    NO_HEADER_ORGANIZATION,
    NOT_FILLED_FIELDS_MESSAGE,
    removeExtraPartFromEntityName,
    resetRowStateDataForEditableTable,
    selectDocumentBranchNames,
    selectDocumentFileUpload,
    selectDocumentProperties,
    selectDocumentTypeOnUpload,
    setFileList,
    uploadDocumentFiles,
    EXsdValidationStatusCodes,
} from 'store/slices/documents-uploading-slice';
import {
    deleteCachedFilesByHashId, deleteCachedFilesById,
    fetchFileDuplicatesInfo,
} from 'store/slices/documents-uploading-slice/api/api';
import {setSessionId} from 'store/slices/documents-uploading-slice/model/slice';
import {closeModal} from 'store/slices/modals-slice';
import {getRegionFromRegionCode, selectRegions} from 'store/slices/regions-slice';
import {tableReportSliceActions} from 'store/slices/table-report-slice/table-report-slice';

import {IEditFormArgs, hasHeaderOrganization, TConfirmModalViews, CONFIRM_MODAL_VIEWS} from '../documents-file-upload-modal-utils';

const getUniqueFieldValue = (file: IDocumentUploadFile) => file[FILE_UNIQUE_FIELD_NAME];

export interface IDocumentsUploadConfig {
    fileSizeFormat: string | null;
    unacceptedFileExtensions?: string[];
    useUploadDate?: boolean;
    xsdValidationEnabled?: boolean;
}

export type TAdditionalFileData =  Record<number, Promise<{
    uploadDate: string;
    correctionNumber: string;
    regNum: string;
} | {
    uploadDate: any;
    correctionNumber: any;
    regNum: any;
}>>;

interface ILoadFileRowFulfilledPayload {
    entityName: string;
    uniqueFieldName: string;
    updatedFile: IDocumentUploadFile;
}

export const useDocumentUploadActions = (
    entityName: string,
    confirmModalViewRef: MutableRefObject<TConfirmModalViews>,
    duplicateFileNamesRef: MutableRefObject<string[]>,
    setIsConfirmModalVisible?: TStateSetter<boolean>,
) => {
    const dispatch = useAppDispatch();
    const metadataSelector = selectMetadata(entityName, EEntityType.EDITABLE_TABLE);
    const [isLoading, setLoading] = useState<boolean>(false);
    const {subsection} = useEntityFromLocation();

    const loadFileRow = (
        tableRow: Readonly<IDocumentUploadFile>,
        en: string,
        additionalFileData: TAdditionalFileData,
    ) => dispatch(loadFileRowStateDataInEditableTable({
        tableRow,
        entityName: en,
        additionalFileData,
    }));

    const clearAllFilesInformation = (
        files: IDocumentUploadFile[],
        // eslint-disable-next-line @typescript-eslint/no-shadow
        entityName: string,
    ) => dispatch(clearAllFilesInfo({entityName}));

    const resetRowState = (tableRow: Record<string, any>) => dispatch(resetRowStateDataForEditableTable({tableRow}));

    const {
        setReportDownloadsData,
        setReadReportDownloadDocuments,
    } = tableReportSliceActions;

    const filterNonRenderableFieldsAndUpdateState = (
        meta: IEntityMeta,
        name: string,
        entityType: EEntityType,
        setFilteredFlag: Dispatch<SetStateAction<boolean>>,
        branchNames?: IDocumentTypeResponse[],
        setDefaultBranchName?: Dispatch<SetStateAction<IDocumentTypeResponse | undefined>>,
    ) => dispatch(filterNonRenderableFieldsAndUpdateStateAction(
        meta,
        name,
        entityType,
        setFilteredFlag,
        branchNames,
        setDefaultBranchName,
    ));
    const createFileListSetter = (
        // eslint-disable-next-line @typescript-eslint/no-shadow
        entityName: string,
    ) => (files: IDocumentUploadFile[]) => dispatch(setFileList({entityName, fileList: files}));

    const {
        regions, documentTypesOnUpload, branchNames, fileUpload, metadata, hasBranchesLinkingFlag,
    } = useSelector((state: TAppState) => ({
        regions: selectRegions(state),
        documentTypesOnUpload: selectDocumentTypeOnUpload(state),
        branchNames: selectDocumentBranchNames(state),
        fileUpload: selectDocumentFileUpload(state),
        hasBranchesLinkingFlag: selectDocumentProperties(state)?.hasBranchesLinkingFlag,
        metadata: metadataSelector(state) as IEntityMeta,
    }));

    const [form, setForm] = useState<FormInstance | undefined>();
    const currentLocation = useLocation<History>();
    const {fileList = [], sessionId} = fileUpload?.[entityName] ?? {};
    const fileListRef = useRef(fileList);
    const fileListSetter = createFileListSetter(entityName);
    const [defaultBranchName, setDefaultBranchName] = useState<IDocumentTypeResponse | undefined>(undefined);
    const [fieldsFiltered, setFieldsFiltered] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [documentsUploadConfig, setDocumentsUploadConfig] = useState<IDocumentsUploadConfig>({
        fileSizeFormat: null,
    });
    const [validation, setValidation] = useState<IValidationController>();
    const additionalFileDataRef = useRef<TAdditionalFileData>({});

    const {validateAllRows} = validation || {};

    const {fields} = metadata ?? {};

    const getFilesWithErrors = () => (fileListRef.current.filter(
        f => (f.xsdValidationStatusCode !== EXsdValidationStatusCodes.SUCCESS
          && f.xsdValidationStatusCode !== EXsdValidationStatusCodes.NOT_APPLICABLE),
    ) ?? []);

    useEffect(() => {
        fileListRef.current = fileList;
    }, [fileList]);

    useEffect(() => {
        fetchFileUploadProperties(setDocumentsUploadConfig);
        dispatch(loadBranchesLinkingFlag());

        if (!sessionId) {
            dispatch(setSessionId({entityName, sessionId: nanoid()}));
        }
    }, []);

    useEffect(() => {
        if (form && branchNames) {
            const formData = form?.getFieldsValue()[EDITABLE_TABLE_LIST_NAME];
            formData?.forEach((item: TEntity, index: number) => {
                if (!branchNames.find(branch => branch.id === item.branchId)) {
                    const defaultBranch = branchNames.find(branch => branch?.attribute1 === 'MAIN');
                    setDefaultBranchName(defaultBranch);
                    form.setFields([{
                        name: [EDITABLE_TABLE_LIST_NAME, index, 'branchId'],
                        value: defaultBranch?.id,
                    }]);
                }
            });
        }
    }, [branchNames]);

    useEffect(() => {
        if (!metadata) {
            dispatch(loadMetadata(entityName, EEntityType.EDITABLE_TABLE));
        }
    }, [entityName, metadata]);

    useEffect(() => {
        if (metadata?.fields && !fieldsFiltered) {
            filterNonRenderableFieldsAndUpdateState(
                metadata,
                metadata.name,
                EEntityType.EDITABLE_TABLE,
                setFieldsFiltered,
                branchNames,
                setDefaultBranchName,
            );
        }
    }, [metadata?.fields]);

    const uploadFiles = (
        files: IDocumentUploadFile[],
        useUploadDate?: boolean,
        shouldUnzip?: boolean,
    ) => dispatch(uploadDocumentFiles({files, entityName, useUploadDate, shouldUnzip}));

    const handleDelete = (tableRow: TEntity) => {
        const fileEntry = tableRow as IDocumentUploadFile;
        const resultFileList = fileListRef.current
            ?.filter((file: IDocumentUploadFile) => (getUniqueFieldValue(file) !== getUniqueFieldValue(fileEntry)));
        fileListSetter(resultFileList);
        resetRowState(fileEntry);

        if (fileEntry.fileUploadInterfaceId) {
            try {
                deleteCachedFilesById(fileEntry.fileUploadInterfaceId);
            } catch (error) {
                console.error('Возникла ошибка при удалении файла из кэша', error);
            }
        }
    };

    const handleEdit = useCallback(async (tableRow: TEntity, args?: IEditFormArgs) => {
        const fileEntryFromTable = tableRow as IDocumentUploadFile;
        const fileIndex = fileListRef.current.findIndex(
            file => getUniqueFieldValue(file) === getUniqueFieldValue(fileEntryFromTable),
        );

        if (fileIndex === -1) return;

        setLoading(true);

        try {
            let updatedFileEntry: IDocumentUploadFile = {...fileEntryFromTable};
            const previousFile = fileListRef.current[fileIndex];
            const regionData = getRegionFromRegionCode(updatedFileEntry.sectionCode, regions);

            // 1. Обновление региона, подразделение, филиала
            if (updatedFileEntry.sectionCode !== previousFile.sectionCode) {
                const defaultSubregion = regionData?.subregionList[0];
                updatedFileEntry.subsectionCode = defaultSubregion?.code;
                updatedFileEntry.subsectionId = defaultSubregion?.id;

                const newSection = regions?.find(type => type.regionCode === updatedFileEntry.sectionCode);
                updatedFileEntry.sectionId = newSection?.id ?? regionData?.id;
            }

            if (updatedFileEntry.subsectionCode !== previousFile.subsectionCode) {
                const newSubsection = regionData?.subregionList.find(
                    subregion => subregion.code === updatedFileEntry.subsectionCode,
                ) ?? regionData?.subregionList[0];
                updatedFileEntry.subsectionId = newSubsection?.id;
            }

            if (args?.branchId) {
                updatedFileEntry.branchId = args.branchId;
            }

            // 2. Обработка смены типа документа
            const hasTypeChanged = updatedFileEntry.type !== previousFile.type;
            if (hasTypeChanged) {
                const docType = documentTypesOnUpload?.find(type => type.id === updatedFileEntry.type);
                if (docType) {
                    updatedFileEntry.title = docType.meaning;
                    updatedFileEntry.typeCode = docType.lookupCode;

                    // Кеш промисов
                    const templateTypesPromises: Record<string, Promise<IDocumentTemplatesTypeResponse[]>> = {};

                    if (!templateTypesPromises[docType.lookupCode]) {
                        templateTypesPromises[docType.lookupCode] = fetchTemplateTypesForFile(docType.lookupCode);
                    }
                    const templates = await templateTypesPromises[docType.lookupCode];

                    updatedFileEntry.templatesTypeOptions = templates;
                    updatedFileEntry.templateCode = templates?.length === 1 ? templates[0].lookupCode : undefined;

                    const loadFileResult = await loadFileRow(
                        updatedFileEntry,
                        entityName,
                        additionalFileDataRef.current,
                    );

                    if (typeof loadFileResult?.payload === 'object' && loadFileResult?.payload?.updatedFile) {
                        updatedFileEntry = loadFileResult?.payload?.updatedFile;
                    }
                }
            }

            // 3. Обновляем файл
            const newFileList = [...fileListRef.current];
            newFileList[fileIndex] = updatedFileEntry;
            fileListSetter(newFileList);

            // 4. Валидация полей
            if (validateAllRows) {
                await validateAllRows(fileListRef.current);
            }
        } catch (error) {
            console.error('Ошибка при редактировании строки:', error);
        } finally {
            setLoading(false);
        }
    }, [regions, documentTypesOnUpload, entityName, loadFileRow, fileListSetter, validateAllRows]);

    const handleUpload = useCallback(debounce(async (_: File, newFiles: File[]) => {
        const filesFilteredByExtensions = filterFilesByExtensions(
            newFiles, documentsUploadConfig.unacceptedFileExtensions,
        );

        const resolveFiles = filesFilteredByExtensions.filter(
            (newFile: File) => !fileListRef.current.some(oldFile => oldFile.fileName === newFile.name),
        );

        if (!resolveFiles.length) return;

        setLoading(true);

        const filesToAdd = resolveFiles.filter((file: File) => file.size > 0);
        const emptyFiles = resolveFiles.filter((file: File) => file.size === 0);

        if (emptyFiles.length > 0) {
            const emptyFileNames = emptyFiles.map(file => file.name).join('\n');
            showMessage({message: `Следующие файлы пустые:\n${emptyFileNames}`, isError: true});
        }

        if (filesToAdd.length === 0) {
            setLoading(false);
            return;
        }

        try {
            const res = await fetchFileAttachInfo(
                removeExtraPartFromEntityName(subsection),
                subsection,
                filesToAdd.map((file: File) => file.name),
            );

            const fileAttachInfo: IFileAttachInfoResponse[] = res.data;

            // Кеш промисов
            const templateTypesPromises: Record<string, Promise<IDocumentTemplatesTypeResponse[]>> = {};

            const newFilesPromises = filesToAdd.map(async (file: File) => {
                const fileInfo = fileAttachInfo.find(el => el.filename === file.name);
                const docType = documentTypesOnUpload?.find(el => el.id === (
                    fileInfo?.defaultDocTypeId as unknown as string
                ));

                let templateTypes: IDocumentTemplatesTypeResponse[] | undefined;

                if (docType) {
                    if (!templateTypesPromises[docType.lookupCode]) {
                        templateTypesPromises[docType.lookupCode] = fetchTemplateTypesForFile(docType.lookupCode);
                    }
                    templateTypes = await templateTypesPromises[docType.lookupCode];
                }

                return generateDefaultFileInfo(
                    file,
                    removeExtraPartFromEntityName(subsection),
                    regions,
                    fileInfo,
                    defaultBranchName,
                    docType,
                    templateTypes,
                );
            });

            const newFilesWithDefaultInfo = await Promise.all(newFilesPromises);

            if (errorMsg === NO_FILES_MESSAGE) {
                setErrorMsg(null);
            }

            // Добавляем новые файлы в список (пока без fileUploadInterfaceId)
            fileListSetter([...fileListRef.current, ...newFilesWithDefaultInfo]);

            // Загружаем строки (получение fileUploadInterfaceId и валидация)
            const loadRowsPromises = newFilesWithDefaultInfo.map(
                file => loadFileRow(file, entityName, additionalFileDataRef.current)
            );
            const loadResults = await Promise.all(loadRowsPromises);

            // Мерджим полученные updatedFile (с fileUploadInterfaceId) в fileList
            const updatedFileList = fileListRef.current.map((file) => {
                const correspondingResult = loadResults.find((r) => {
                    const payload = r.payload as ILoadFileRowFulfilledPayload | undefined;
                    return payload?.updatedFile?.fileName === file?.fileName;
                });

                if (correspondingResult) {
                    const {updatedFile} = correspondingResult.payload as ILoadFileRowFulfilledPayload;
                    return {...file, ...updatedFile};
                }
                return file;
            });

            fileListSetter(updatedFileList);
        } catch (e) {
            if (e?.response?.status === 422) {
                showMessageFromResponse({response: e.response, isError: true});
                return;
            }

            console.error('File attach info error:', e);
            setErrorMsg('Failed to get file information');
        } finally {
            setLoading(false);
        }
    }, 500), [
        documentTypesOnUpload,
        regions,
        defaultBranchName,
        entityName,
        currentLocation,
        documentsUploadConfig,
        errorMsg,
        fileList,
        fileListSetter,
        loadFileRow,
        sessionId,
        subsection,
    ]);

    const validateRequiredFieldsManually = useCallback(() => {
        fileListRef.current.forEach(file => {
            ['correctionNumber', 'uploadDate'].forEach(fieldKey => {
                if (fieldKey === 'uploadDate' && !documentsUploadConfig.useUploadDate) return;
                const activityList = fileUpload?.[entityName]?.activityList;
                const fieldPermissions = activityList?.[file.fileName]?.editFieldsPermission ?? {};

                if (fieldPermissions?.[fieldKey]?.activated) {
                    const field = fields?.find(f => f.key === fieldKey);
                    if (field?.required && !file[fieldKey]) {
                        throw new Error('Required fields are not filled.');
                    }
                }
            });
        });
    }, [fileList, fileUpload, entityName, fields]);

    const handleSubmit = async () => {
        if (!hasHeaderOrganization(hasBranchesLinkingFlag, branchNames)) {
            setErrorMsg(NO_HEADER_ORGANIZATION);
            return;
        }

        try {
            if (fileListRef.current.length) {
                const isValid = validateAllRows ? await validateAllRows(fileListRef.current) : false;
                if (!isValid) throw new Error('Ошибка при попытке валидации строк таблицы');
                validateRequiredFieldsManually();
                setErrorMsg(null);
                await uploadFiles(fileListRef.current, documentsUploadConfig.useUploadDate, true);
                clearAllFilesInformation(fileListRef.current, entityName);
                dispatch(closeModal());
                await fetchRequestListInfo()
                    .then(result => {
                        dispatch(setReportDownloadsData(result.data));
                        dispatch(setReadReportDownloadDocuments(
                            {wereDocumentsRead: false, isAnimationActive: true},
                        ));
                    })
                    .catch(er => showMessageFromResponse({
                        response: er?.response,
                        isError: true,
                    }));
            } else {
                setErrorMsg(NO_FILES_MESSAGE);
            }
        } catch (e) {
            setErrorMsg(NOT_FILLED_FIELDS_MESSAGE);
            console.error(e);
        }
    };

    const handleValidation = async () => {
        const errorFilesList = getFilesWithErrors();

        const {data: duplicateFileNames} = await fetchFileDuplicatesInfo(fileList.map(
          (f) => ({
            docTypeId: f.type,
            fileUploadInterfaceId: f.fileUploadInterfaceId,
            corrNum: f.correctionNumber,
          })
        ));

        duplicateFileNamesRef.current = duplicateFileNames;

        if (duplicateFileNames.length > 0 && setIsConfirmModalVisible) {
            confirmModalViewRef.current = CONFIRM_MODAL_VIEWS.DUPLICATE_VALIDATION;

            setIsConfirmModalVisible(true);
            return;
        }

        if (errorFilesList.length > 0 && setIsConfirmModalVisible) {
            confirmModalViewRef.current = CONFIRM_MODAL_VIEWS.XSD_VALIDATION;

            setIsConfirmModalVisible(true);
            return;
        }

        handleSubmit();
    };

    const handleClear = () => {
        setForm(undefined);
        setErrorMsg(null);
        clearAllFilesInformation(fileListRef.current, entityName);

        if (sessionId) {
            try {
                deleteCachedFilesByHashId(sessionId);
            } catch (error) {
                console.error('Возникла ошибка при удалении файлов из кэша', error);
            }
        }
    };

    return {
        isLoading,
        fileList,
        errorMsg,
        fieldsFiltered,
        handleUpload,
        form,
        setForm,
        validation,
        setValidation,
        handleEdit,
        handleClear,
        handleDelete,
        handleSubmit,
        handleValidation,
        isPageJumperEnabled: metadata?.isPageJumperEnabled,
        isPageSizeChangerEnabled: metadata?.isPageSizeChangerEnabled,
        actions: metadata?.actions,
        useUploadDate: documentsUploadConfig.useUploadDate,
        fileSizeFormat: documentsUploadConfig.fileSizeFormat,
        filesWithError: getFilesWithErrors(),
        xsdValidationEnabled: documentsUploadConfig.xsdValidationEnabled,
    };
};
